/** Route path constants and path helpers — no navigation policy. */

/** Route constants - shared across all features. */

import { HUB_PERMISSION_KEYS, type HubPermission } from "@/lib/auth/permissions";
import { PLATFORM_ROUTES } from './platform';
import { INTAKE_ROUTES } from './intake';
import { WORK_ROUTES } from './work';
import { STRATEGY_ROUTES } from './strategy';
import { PARTIES_ROUTES } from './parties';
import { GOVERNANCE_ROUTES } from './governance';
import { CONTRACTS_ROUTES } from './contracts';
import { FINANCE_ROUTES } from './finance';
import { HRM_ROUTES } from './hrm';
import { KNOWLEDGE_ROUTES } from './knowledge';
import { AI_ROUTES } from './ai';
import { ADMIN_ROUTES } from './admin';
import { OTHER_ROUTES } from './other';

export const ROUTES = {
  ...PLATFORM_ROUTES,
  ...INTAKE_ROUTES,
  ...WORK_ROUTES,
  ...STRATEGY_ROUTES,
  ...PARTIES_ROUTES,
  ...GOVERNANCE_ROUTES,
  ...CONTRACTS_ROUTES,
  ...FINANCE_ROUTES,
  ...HRM_ROUTES,
  ...KNOWLEDGE_ROUTES,
  ...AI_ROUTES,
  ...ADMIN_ROUTES,
  ...OTHER_ROUTES,
} as const;



/** DEC-P0-FE-MODULE-RAILS-001/004/005/006/007: content-capability SoT (ModuleRails allowlist). */
export type HrmModuleId = "M01" | "M02" | "M03" | "M04" | "M05" | "M06" | "M07" | "M08";
export type HrmSubgroupId = "catalog" | "process";
export type KnowledgeModuleId = "corpus" | "content" | "ops";
export type PartiesModuleId = "party-master" | "supplier-ops" | "partner-ops";
export type PolicyModuleId =
  | "template-governance"
  | "ai-safety"
  | "policy-rules"
  | "platform-administration";
export type IamModuleId = "tenants" | "rbac" | "catalog" | "verify";
export type AiModuleId = "ai";
export type IntakeModuleId = "intake" | "mailbox" | "conversations";
export type DataGovernanceModuleId = "knowledge-data-governance";
export type WorkModuleId = "work";
export type StrategyModuleId = "strategy";
export type FinanceModuleId = "finance";
export type ContractsModuleId = "contracts";
export type ObservabilityModuleId = "observability";
export type ContentCapabilityId =
  | "hrm"
  | "knowledge-platform"
  | "customer-intelligence"
  | "platform-governance"
  | "platform-administration"
  | "iam"
  | "ai"
  | "intake-core"
  | "knowledge-data-governance"
  | "work-orchestration"
  | "strategy"
  | "finance"
  | "contracts"
  | "observability";

export interface ContentCapabilityConfig {
  capabilityId: ContentCapabilityId;
  l2Ids: readonly string[];
  landingPaths: Record<string, string>;
  labelKeyByL2: Record<string, string>;
  resolveClusterFromPath: (pathname: string) => string | null;
  subgroups?: readonly HrmSubgroupId[];
}

export const HRM_L2_MODULES: readonly HrmModuleId[] = [
  "M01",
  "M02",
  "M03",
  "M04",
  "M05",
  "M06",
  "M07",
  "M08",
];

export const KNOWLEDGE_L2_MODULES: readonly KnowledgeModuleId[] = ["corpus", "content", "ops"];

export const PARTIES_L2_MODULES: readonly PartiesModuleId[] = [
  "party-master",
  "supplier-ops",
  "partner-ops",
];

export const POLICY_L2_MODULES: readonly PolicyModuleId[] = [
  "template-governance",
  "ai-safety",
  "policy-rules",
];

export const PLATFORM_ADMIN_L2_MODULES: readonly PolicyModuleId[] = ["platform-administration"];

export const IAM_L2_MODULES: readonly IamModuleId[] = [
  "tenants",
  "rbac",
  "catalog",
  "verify",
];

export const AI_L2_MODULES: readonly AiModuleId[] = ["ai"];

export const INTAKE_L2_MODULES: readonly IntakeModuleId[] = ["intake", "mailbox", "conversations"];

export const DATA_GOVERNANCE_L2_MODULES: readonly DataGovernanceModuleId[] = ["knowledge-data-governance"];

export const WORK_L2_MODULES: readonly WorkModuleId[] = ["work"];

export const STRATEGY_L2_MODULES: readonly StrategyModuleId[] = ["strategy"];

export const FINANCE_L2_MODULES: readonly FinanceModuleId[] = ["finance"];

export const CONTRACTS_L2_MODULES: readonly ContractsModuleId[] = ["contracts"];

export const OBSERVABILITY_L2_MODULES: readonly ObservabilityModuleId[] = ["observability"];

const HRM_M03_PATHS = new Set<string>([
  ROUTES.HRM_REGIONAL_MINIMUM_WAGES,
  ROUTES.HRM_SI_PARTICIPATION_RATES,
  ROUTES.HRM_SOCIAL_INSURANCE_HISTORIES,
]);
const HRM_M05_PATHS = new Set<string>([
  ROUTES.HRM_PAYROLL_PERIODS,
  ROUTES.HRM_PAYROLL_ITEM_GROUPS,
  ROUTES.HRM_PAYROLL_ITEMS,
  ROUTES.HRM_PAYROLL_ITEM_FORMULAS,
  ROUTES.HRM_SI_MONTHLY_CONTRIBUTIONS,
  ROUTES.HRM_EXTERNAL_INCOME_INDIVIDUALS,
  ROUTES.HRM_EXTERNAL_INCOME_BLOCKS,
  ROUTES.HRM_SALARY_PLAN_ENTRIES,
  ROUTES.HRM_CLASSIFIED_REVENUES,
  ROUTES.HRM_KY1_BENEFIT_RATES,
  ROUTES.HRM_HOSPITAL_MONTHLY_REVENUES,
  ROUTES.HRM_PAYROLL_SNAPSHOTS,
  ROUTES.HRM_ABR_PHASE,
]);
const HRM_M07_PATHS = new Set<string>([
  ROUTES.HRM_ALERTS,
  ROUTES.HRM_FORM_LAYOUTS,
  ROUTES.HRM_REPORT_LAYOUTS,
]);

export const HRM_MODULE_LANDING_PATHS: Record<HrmModuleId, string> = {
  M01: ROUTES.HRM_HEADCOUNTS,
  M02: ROUTES.HRM_EMPLOYEES,
  M03: ROUTES.HRM_SOCIAL_INSURANCE_HISTORIES,
  M04: ROUTES.HRM_ATTENDANCE_PERIODS,
  M05: ROUTES.HRM_PAYROLL_PERIODS,
  M06: ROUTES.HRM_LEAVE_REQUESTS,
  M07: ROUTES.HRM_ALERTS,
  M08: ROUTES.HRM_HIS_REVENUE,
};

export const HRM_MODULE_SETUP_LANDING_PATHS: Record<Exclude<HrmModuleId, "M06">, string> = {
  M01: ROUTES.HRM_LEGAL_ENTITIES,
  M02: ROUTES.HRM_BANKS,
  M03: ROUTES.HRM_SI_PARTICIPATION_RATES,
  M04: ROUTES.HRM_ATTENDANCE_OBJECTS,
  M05: ROUTES.HRM_PAYROLL_ITEM_GROUPS,
  M07: ROUTES.HRM_FORM_LAYOUTS,
  M08: ROUTES.HRM_AD_LDAP,
};

export function hrmHouseLandingPath(moduleId: HrmModuleId, house: HrmSubgroupId): string {
  if (house === "process") return HRM_MODULE_LANDING_PATHS[moduleId];
  if (moduleId === "M06") return HRM_MODULE_LANDING_PATHS.M06;
  return HRM_MODULE_SETUP_LANDING_PATHS[moduleId];
}

export const KNOWLEDGE_MODULE_LANDING_PATHS: Record<KnowledgeModuleId, string> = {
  corpus: ROUTES.KNOWLEDGE_CORPUS,
  content: ROUTES.KNOWLEDGE_CONTENT,
  ops: ROUTES.KNOWLEDGE_OPS,
};

export const PARTIES_MODULE_LANDING_PATHS: Record<PartiesModuleId, string> = {
  "party-master": ROUTES.PARTIES,
  "supplier-ops": ROUTES.SUPPLIER_DIRECTORY,
  "partner-ops": ROUTES.PARTNER_DIRECTORY,
};

export const POLICY_MODULE_LANDING_PATHS: Record<PolicyModuleId, string> = {
  "template-governance": ROUTES.GOVERNANCE_DRAFT_TEMPLATES,
  "ai-safety": ROUTES.GOVERNANCE_AI_AUDIT,
  "policy-rules": ROUTES.GOVERNANCE_TEMPLATE_ROUTING_RULES,
  "platform-administration": ROUTES.GOVERNANCE_SYNC_DIAGNOSTICS,
};

export const IAM_MODULE_LANDING_PATHS: Record<IamModuleId, string> = {
  tenants: ROUTES.ADMIN_AUTHZ_TENANTS,
  rbac: ROUTES.ADMIN_AUTHZ_ROLES,
  catalog: ROUTES.ADMIN_AUTHZ_SERVICES,
  verify: ROUTES.ADMIN_AUTHZ_SIMULATOR,
};

export const AI_MODULE_LANDING_PATHS: Record<AiModuleId, string> = {
  ai: ROUTES.AI_CHAT,
};

export const INTAKE_MODULE_LANDING_PATHS: Record<IntakeModuleId, string> = {
  intake: ROUTES.DASHBOARD,
  mailbox: ROUTES.MAILBOX,
  conversations: ROUTES.CONVERSATIONS,
};

export const DATA_GOVERNANCE_MODULE_LANDING_PATHS: Record<DataGovernanceModuleId, string> = {
  "knowledge-data-governance": ROUTES.GOVERNANCE_CONTEXT_CATALOG,
};

export const WORK_MODULE_LANDING_PATHS: Record<WorkModuleId, string> = {
  work: ROUTES.WORK_PROJECTS,
};

export const STRATEGY_MODULE_LANDING_PATHS: Record<StrategyModuleId, string> = {
  strategy: ROUTES.STRATEGY,
};

export const FINANCE_MODULE_LANDING_PATHS: Record<FinanceModuleId, string> = {
  finance: ROUTES.FINANCE_INVOICES,
};

export const CONTRACTS_MODULE_LANDING_PATHS: Record<ContractsModuleId, string> = {
  contracts: ROUTES.CONTRACTS,
};

export const OBSERVABILITY_MODULE_LANDING_PATHS: Record<ObservabilityModuleId, string> = {
  observability: ROUTES.PLATFORM_MONITORING,
};

const HRM_LABEL_KEY: Record<HrmModuleId, string> = {
  M01: "nav.hrmModuleM01",
  M02: "nav.hrmModuleM02",
  M03: "nav.hrmModuleM03",
  M04: "nav.hrmModuleM04",
  M05: "nav.hrmModuleM05",
  M06: "nav.hrmModuleM06",
  M07: "nav.hrmModuleM07",
  M08: "nav.hrmModuleM08",
};

const KNOWLEDGE_LABEL_KEY: Record<KnowledgeModuleId, string> = {
  corpus: "nav.knowledgeModuleCorpus",
  content: "nav.knowledgeModuleContent",
  ops: "nav.knowledgeModuleOps",
};

const PARTIES_LABEL_KEY: Record<PartiesModuleId, string> = {
  "party-master": "navSection.partyMaster",
  "supplier-ops": "navSection.supplierOps",
  "partner-ops": "navSection.partnerOps",
};

const POLICY_LABEL_KEY: Record<PolicyModuleId, string> = {
  "template-governance": "navSection.templateGovernance",
  "ai-safety": "navSection.aiSafety",
  "policy-rules": "navSection.policyRules",
  "platform-administration": "navSection.platformAdministration",
};

const IAM_LABEL_KEY: Record<IamModuleId, string> = {
  tenants: "navSection.iamTenants",
  rbac: "navSection.iamRbac",
  catalog: "navSection.iamCatalog",
  verify: "navSection.iamVerify",
};

const AI_LABEL_KEY: Record<AiModuleId, string> = {
  ai: "navSection.aiPlatform",
};

const INTAKE_LABEL_KEY: Record<IntakeModuleId, string> = {
  intake: "navSection.intake",
  mailbox: "navSection.intakeMailbox",
  conversations: "navSection.conversations",
};

const DATA_GOVERNANCE_LABEL_KEY: Record<DataGovernanceModuleId, string> = {
  "knowledge-data-governance": "navSection.knowledgeDataGovernance",
};

const WORK_LABEL_KEY: Record<WorkModuleId, string> = {
  work: "navSection.work",
};

const STRATEGY_LABEL_KEY: Record<StrategyModuleId, string> = {
  strategy: "navSection.strategy",
};

const FINANCE_LABEL_KEY: Record<FinanceModuleId, string> = {
  finance: "navSection.finance",
};

const CONTRACTS_LABEL_KEY: Record<ContractsModuleId, string> = {
  contracts: "navSection.contracts",
};

const OBSERVABILITY_LABEL_KEY: Record<ObservabilityModuleId, string> = {
  observability: "navSection.observability",
};

/** Prompt Governance leaf path → templates `kind` query (router Navigate SoT). */
export const POLICY_TEMPLATE_LEAF_KIND: Readonly<Record<string, string>> = {
  [ROUTES.GOVERNANCE_DRAFT_TEMPLATES]: "draft",
  [ROUTES.GOVERNANCE_TRIAGE_TEMPLATES]: "triage",
  [ROUTES.GOVERNANCE_THREAD_DIGEST_TEMPLATES]: "thread_digest",
  [ROUTES.GOVERNANCE_REQUEST_INFO_TEMPLATES]: "request_info",
};

/** Canonical in-app href for a Policy template leaf (avoids redirect flash). */
export function policyTemplateLeafHref(leafPath: string): string {
  const kind = POLICY_TEMPLATE_LEAF_KIND[leafPath];
  if (kind === undefined) return leafPath;
  if (kind === "draft") return ROUTES.GOVERNANCE_DRAFT_TEMPLATES;
  return `${ROUTES.GOVERNANCE_DRAFT_TEMPLATES}?kind=${encodeURIComponent(kind)}`;
}

/** Active kind on Prompt Governance surface; missing/blank query → draft. */
export function policyTemplateActiveKind(pathname: string, search: string): string | null {
  const path = pathname.split("?")[0];
  if (path !== ROUTES.GOVERNANCE_DRAFT_TEMPLATES) return null;
  const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("kind");
  const kind = (raw ?? "").trim();
  return kind.length > 0 ? kind : "draft";
}

/** Fail-closed: prefix peers must not nest-activate a leaf (DEC-P0-FE-MODULE-RAILS-003/005). */
export const PATH_ACTIVE_PEER_EXCLUSIONS: Readonly<Record<string, readonly string[]>> = {
  [ROUTES.PARTIES]: [ROUTES.PARTY_IDENTITY_REVIEW, ROUTES.SUPPLIER_DIRECTORY, ROUTES.PARTNER_DIRECTORY],
  [ROUTES.CONVERSATIONS]: [ROUTES.CONVERSATIONS_CHANNEL_SCOPE],
  [ROUTES.GOVERNANCE_DRAFT_TEMPLATES]: [
    ROUTES.GOVERNANCE_TRIAGE_TEMPLATES,
    ROUTES.GOVERNANCE_THREAD_DIGEST_TEMPLATES,
    ROUTES.GOVERNANCE_REQUEST_INFO_TEMPLATES,
  ],
  [ROUTES.KNOWLEDGE_CONTENT]: [
    ROUTES.KNOWLEDGE_CONTENT_CAPTURE,
    ROUTES.KNOWLEDGE_CONTENT_REVIEW,
    ROUTES.KNOWLEDGE_CONTENT_LIBRARY,
    ROUTES.KNOWLEDGE_CONTENT_STORAGE,
  ],
  [ROUTES.STRATEGY]: [ROUTES.STRATEGY_GOALS, ROUTES.STRATEGY_ALIGNMENT, ROUTES.STRATEGY_REVIEWS],
  [ROUTES.CONTRACTS]: [ROUTES.CONTRACTS_TEMPLATES],
  [ROUTES.FINANCE_BANK]: [ROUTES.FINANCE_BANK_TRANSACTIONS],
};


/** Fail-closed path → HRM L2; unknown /hrm paths return null. */
export function resolveHrmModuleFromPath(pathname: string): HrmModuleId | null {
  const path = pathname.split("?")[0];
  if (path.startsWith("/hrm/org/") || path === "/hrm/org") return "M01";
  if (path.startsWith("/hrm/portal/") || path === "/hrm/portal") return "M06";
  if (path.startsWith("/hrm/integrations/") || path === "/hrm/integrations") return "M08";
  if (HRM_M03_PATHS.has(path)) return "M03";
  if (path.startsWith("/hrm/hr/attendance-")) return "M04";
  if (
    path === ROUTES.HRM_OT_TIME_WINDOWS ||
    path === ROUTES.HRM_OT_REASONS ||
    path === ROUTES.HRM_OT_PLANS ||
    path === ROUTES.HRM_OT_PLAN_LINES ||
    path === ROUTES.HRM_COLLECTIVE_BUSINESS_TRIPS ||
    path === ROUTES.HRM_COLLECTIVE_BUSINESS_TRIP_LINES ||
    path === ROUTES.HRM_DUTY_CONVERSION_ENTRIES ||
    path === ROUTES.HRM_TIMESHEET_DAILY_ENTRIES ||
    path === ROUTES.HRM_LATE_EARLY_VIOLATIONS ||
    path === ROUTES.HRM_DUTY_FUND_LEDGERS ||
    path === ROUTES.HRM_COMP_LEAVE_FUND_LEDGERS ||
    path === ROUTES.HRM_FUND_OPENING_BALANCES
  ) {
    return "M04";
  }
  if (HRM_M05_PATHS.has(path)) return "M05";
  if (HRM_M07_PATHS.has(path)) return "M07";
  if (path.startsWith("/hrm/hr/") || path === "/hrm/hr") return "M02";
  if (path === "/hrm" || path.startsWith("/hrm/")) return null;
  return null;
}

/** Fail-closed path → Knowledge L2; unknown /knowledge paths return null. */
export function resolveKnowledgeModuleFromPath(pathname: string): KnowledgeModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.KNOWLEDGE_CONTENT || path.startsWith(`${ROUTES.KNOWLEDGE_CONTENT}/`)) return "content";
  if (
    path === ROUTES.KNOWLEDGE_OPS ||
    path === ROUTES.KNOWLEDGE_EMBEDDINGS ||
    path === ROUTES.KNOWLEDGE_SEARCH_LAB ||
    path === ROUTES.KNOWLEDGE_EVALUATION
  ) {
    return "ops";
  }
  if (
    path === ROUTES.KNOWLEDGE_CORPUS ||
    path.startsWith(`${ROUTES.KNOWLEDGE_CORPUS}/`) ||
    path === ROUTES.KNOWLEDGE_PRE_RAG_REVIEW ||
    path.startsWith(`${ROUTES.KNOWLEDGE_PRE_RAG_REVIEW}/`) ||
    path === ROUTES.KNOWLEDGE_INGEST
  ) {
    return "corpus";
  }
  if (path === ROUTES.KNOWLEDGE_PLATFORM || path.startsWith(`${ROUTES.KNOWLEDGE_PLATFORM}/`)) return null;
  return null;
}

/** Fail-closed path → Parties L2; unknown commercial-party paths return null. */
export function resolvePartiesModuleFromPath(pathname: string): PartiesModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.SUPPLIER_DIRECTORY || path.startsWith(`${ROUTES.SUPPLIER_DIRECTORY}/`)) return "supplier-ops";
  if (path === ROUTES.PARTNER_DIRECTORY || path.startsWith(`${ROUTES.PARTNER_DIRECTORY}/`)) return "partner-ops";
  if (path === ROUTES.PARTIES || path.startsWith(`${ROUTES.PARTIES}/`)) return "party-master";
  return null;
}

/** Fail-closed path → Policy L2; unknown governance paths return null. */
export function resolvePolicyModuleFromPath(pathname: string): PolicyModuleId | null {
  const path = pathname.split("?")[0];
  if (
    path === ROUTES.GOVERNANCE_DRAFT_TEMPLATES ||
    path === ROUTES.GOVERNANCE_TRIAGE_TEMPLATES ||
    path === ROUTES.GOVERNANCE_THREAD_DIGEST_TEMPLATES ||
    path === ROUTES.GOVERNANCE_REQUEST_INFO_TEMPLATES
  ) {
    return "template-governance";
  }
  if (path === ROUTES.GOVERNANCE_AI_AUDIT || path.startsWith(`${ROUTES.GOVERNANCE_AI_AUDIT}/`)) return "ai-safety";
  if (path === ROUTES.GOVERNANCE_AI_PRIVACY_RULES || path === "/llm-privacy") return "ai-safety";
  if (path === ROUTES.AI_WORKFLOW_POLICY || path === "/llm-runtime" || path === "/admin/llm-runtime") return "ai-safety";
  if (path === ROUTES.GOVERNANCE_TEMPLATE_ROUTING_RULES || path === ROUTES.ADMIN_TEMPLATE_ROUTING_RULES) {
    return "policy-rules";
  }
  if (
    path === ROUTES.GOVERNANCE_OPERATIONAL_SETTINGS ||
    path === ROUTES.GOVERNANCE_INTEGRATIONS ||
    path === ROUTES.GOVERNANCE_SYNC_DIAGNOSTICS
  ) {
    return "platform-administration";
  }
  return null;
}

/** Fail-closed path → IAM L2; /iam index and unknown leaves return null. */
export function resolveIamModuleFromPath(pathname: string): IamModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.ADMIN_AUTHZ_TENANTS || path.startsWith(`${ROUTES.ADMIN_AUTHZ_TENANTS}/`)) {
    return "tenants";
  }
  if (
    path === ROUTES.ADMIN_AUTHZ_ROLES ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_ROLES}/`) ||
    path === ROUTES.ADMIN_AUTHZ_USERS ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_USERS}/`)
  ) {
    return "rbac";
  }
  if (
    path === ROUTES.ADMIN_AUTHZ_SERVICES ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_SERVICES}/`) ||
    path === ROUTES.ADMIN_AUTHZ_SERVICE_ITEMS ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_SERVICE_ITEMS}/`) ||
    path === ROUTES.ADMIN_AUTHZ_SYNC ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_SYNC}/`)
  ) {
    return "catalog";
  }
  if (
    path === ROUTES.ADMIN_AUTHZ_SIMULATOR ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_SIMULATOR}/`) ||
    path === ROUTES.ADMIN_AUTHZ_AUDIT ||
    path.startsWith(`${ROUTES.ADMIN_AUTHZ_AUDIT}/`)
  ) {
    return "verify";
  }
  return null;
}

/** Fail-closed path → AI L2; governance-owned `/ai/runtime` excluded. */
export function resolveAiModuleFromPath(pathname: string): AiModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.AI_WORKFLOW_POLICY || path === "/llm-runtime" || path === "/admin/llm-runtime") return null;
  if (path === "/ai" || path.startsWith("/ai/")) return "ai";
  return null;
}

/** Fail-closed path → Intake L2; only canonical flat Intake surfaces (not `/` or `/admin/ledger`). */
export function resolveIntakeModuleFromPath(pathname: string): IntakeModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.DASHBOARD) return "intake";
  if (path === ROUTES.INTAKE_RECORDS || path.startsWith(`${ROUTES.INTAKE_RECORDS}/`)) return "intake";
  if (path === ROUTES.INTAKE_DRAFTS || path.startsWith(`${ROUTES.INTAKE_DRAFTS}/`)) return "intake";
  if (path === ROUTES.INTAKE_DIGEST || path.startsWith(`${ROUTES.INTAKE_DIGEST}/`)) return "intake";
  if (path === ROUTES.INTAKE_LEDGER || path.startsWith(`${ROUTES.INTAKE_LEDGER}/`)) return "intake";
  if (path === ROUTES.MAILBOX) return "mailbox";
  if (path === ROUTES.CONVERSATIONS || path.startsWith(`${ROUTES.CONVERSATIONS}/`)) return "conversations";
  return null;
}

/** Fail-closed path → Data Governance L2; only `/governance/knowledge/*` (not Policy `/governance` admin). */
export function resolveDataGovernanceModuleFromPath(pathname: string): DataGovernanceModuleId | null {
  const path = pathname.split("?")[0];
  const prefix = "/governance/knowledge";
  if (path === prefix || path.startsWith(`${prefix}/`)) return "knowledge-data-governance";
  return null;
}

/** Fail-closed path → Work L2; only Work Orchestration surfaces (not Observability `/monitoring`). */
export function resolveWorkModuleFromPath(pathname: string): WorkModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.WORK_PROJECTS || path.startsWith(`${ROUTES.WORK_PROJECTS}/`)) return "work";
  if (path === ROUTES.WORK_TASKS || path.startsWith(`${ROUTES.WORK_TASKS}/`)) return "work";
  if (path === ROUTES.WORK_CUSTOM_VIEW || path.startsWith(`${ROUTES.WORK_CUSTOM_VIEW}/`)) return "work";
  if (path === ROUTES.WORK_ANALYTICS || path.startsWith(`${ROUTES.WORK_ANALYTICS}/`)) return "work";
  return null;
}

/** Fail-closed path → Strategy L2; only `/strategy` and `/strategy/*`. */
export function resolveStrategyModuleFromPath(pathname: string): StrategyModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.STRATEGY || path.startsWith(`${ROUTES.STRATEGY}/`)) return "strategy";
  return null;
}

/** Fail-closed path → Finance L2; only `/finance` and `/finance/*`. */
export function resolveFinanceModuleFromPath(pathname: string): FinanceModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.FINANCE || path.startsWith(`${ROUTES.FINANCE}/`)) return "finance";
  return null;
}

/** Fail-closed path → Contracts L2; only `/contracts` and `/contracts/*`. */
export function resolveContractsModuleFromPath(pathname: string): ContractsModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.CONTRACTS || path.startsWith(`${ROUTES.CONTRACTS}/`)) return "contracts";
  return null;
}

/** Fail-closed path → Observability L2; platform `/monitoring` + `/analytics` only (not Work `/operate/analytics`). */
export function resolveObservabilityModuleFromPath(pathname: string): ObservabilityModuleId | null {
  const path = pathname.split("?")[0];
  if (path === ROUTES.PLATFORM_MONITORING || path.startsWith(`${ROUTES.PLATFORM_MONITORING}/`)) return "observability";
  if (path === ROUTES.PLATFORM_ANALYTICS || path.startsWith(`${ROUTES.PLATFORM_ANALYTICS}/`)) return "observability";
  return null;
}

export const CONTENT_CAPABILITIES: Record<ContentCapabilityId, ContentCapabilityConfig> = {
  hrm: {
    capabilityId: "hrm",
    l2Ids: HRM_L2_MODULES,
    landingPaths: HRM_MODULE_LANDING_PATHS,
    labelKeyByL2: HRM_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveHrmModuleFromPath(pathname),
    subgroups: ["catalog", "process"],
  },
  "knowledge-platform": {
    capabilityId: "knowledge-platform",
    l2Ids: KNOWLEDGE_L2_MODULES,
    landingPaths: KNOWLEDGE_MODULE_LANDING_PATHS,
    labelKeyByL2: KNOWLEDGE_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveKnowledgeModuleFromPath(pathname),
  },
  "customer-intelligence": {
    capabilityId: "customer-intelligence",
    l2Ids: PARTIES_L2_MODULES,
    landingPaths: PARTIES_MODULE_LANDING_PATHS,
    labelKeyByL2: PARTIES_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolvePartiesModuleFromPath(pathname),
  },
  "platform-governance": {
    capabilityId: "platform-governance",
    l2Ids: POLICY_L2_MODULES,
    landingPaths: POLICY_MODULE_LANDING_PATHS,
    labelKeyByL2: POLICY_LABEL_KEY,
    resolveClusterFromPath: (pathname) => {
      const moduleId = resolvePolicyModuleFromPath(pathname);
      return moduleId === "platform-administration" ? null : moduleId;
    },
  },
  "platform-administration": {
    capabilityId: "platform-administration",
    l2Ids: PLATFORM_ADMIN_L2_MODULES,
    landingPaths: { "platform-administration": POLICY_MODULE_LANDING_PATHS["platform-administration"] },
    labelKeyByL2: { "platform-administration": POLICY_LABEL_KEY["platform-administration"] },
    resolveClusterFromPath: (pathname) => {
      const moduleId = resolvePolicyModuleFromPath(pathname);
      return moduleId === "platform-administration" ? moduleId : null;
    },
  },
  iam: {
    capabilityId: "iam",
    l2Ids: IAM_L2_MODULES,
    landingPaths: IAM_MODULE_LANDING_PATHS,
    labelKeyByL2: IAM_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveIamModuleFromPath(pathname),
  },
  ai: {
    capabilityId: "ai",
    l2Ids: AI_L2_MODULES,
    landingPaths: AI_MODULE_LANDING_PATHS,
    labelKeyByL2: AI_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveAiModuleFromPath(pathname),
  },
  "intake-core": {
    capabilityId: "intake-core",
    l2Ids: INTAKE_L2_MODULES,
    landingPaths: INTAKE_MODULE_LANDING_PATHS,
    labelKeyByL2: INTAKE_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveIntakeModuleFromPath(pathname),
  },
  "knowledge-data-governance": {
    capabilityId: "knowledge-data-governance",
    l2Ids: DATA_GOVERNANCE_L2_MODULES,
    landingPaths: DATA_GOVERNANCE_MODULE_LANDING_PATHS,
    labelKeyByL2: DATA_GOVERNANCE_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveDataGovernanceModuleFromPath(pathname),
  },
  "work-orchestration": {
    capabilityId: "work-orchestration",
    l2Ids: WORK_L2_MODULES,
    landingPaths: WORK_MODULE_LANDING_PATHS,
    labelKeyByL2: WORK_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveWorkModuleFromPath(pathname),
  },
  strategy: {
    capabilityId: "strategy",
    l2Ids: STRATEGY_L2_MODULES,
    landingPaths: STRATEGY_MODULE_LANDING_PATHS,
    labelKeyByL2: STRATEGY_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveStrategyModuleFromPath(pathname),
  },
  finance: {
    capabilityId: "finance",
    l2Ids: FINANCE_L2_MODULES,
    landingPaths: FINANCE_MODULE_LANDING_PATHS,
    labelKeyByL2: FINANCE_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveFinanceModuleFromPath(pathname),
  },
  contracts: {
    capabilityId: "contracts",
    l2Ids: CONTRACTS_L2_MODULES,
    landingPaths: CONTRACTS_MODULE_LANDING_PATHS,
    labelKeyByL2: CONTRACTS_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveContractsModuleFromPath(pathname),
  },
  observability: {
    capabilityId: "observability",
    l2Ids: OBSERVABILITY_L2_MODULES,
    landingPaths: OBSERVABILITY_MODULE_LANDING_PATHS,
    labelKeyByL2: OBSERVABILITY_LABEL_KEY,
    resolveClusterFromPath: (pathname) => resolveObservabilityModuleFromPath(pathname),
  },
};

export const CONTENT_CAPABILITY_IDS: readonly ContentCapabilityId[] = [
  "hrm",
  "knowledge-platform",
  "customer-intelligence",
  "platform-governance",
  "platform-administration",
  "iam",
  "ai",
  "intake-core",
  "knowledge-data-governance",
  "work-orchestration",
  "strategy",
  "finance",
  "contracts",
  "observability",
];

export function usesContentCapability(capabilityId: string): boolean {
  return (CONTENT_CAPABILITY_IDS as readonly string[]).includes(capabilityId);
}

export function getContentCapability(capabilityId: ContentCapabilityId): ContentCapabilityConfig {
  return CONTENT_CAPABILITIES[capabilityId];
}

/** Base Style IA class per Platform L1 id (DEC-P0-FE-MODULE-RAILS-002). Not dual-menu allowlist. */
export type BaseStyleNavClass = "content-capability" | "module-section" | "sidebar-expand" | "surface";

export const BASE_STYLE_L1_CLASS: Readonly<Record<string, BaseStyleNavClass>> = {
  dashboard: "surface",
  search: "surface",
  ai: "content-capability",
  "knowledge-platform": "content-capability",
  "knowledge-data-governance": "content-capability",
  "intake-core": "content-capability",
  "customer-intelligence": "content-capability",
  "work-orchestration": "content-capability",
  strategy: "content-capability",
  contracts: "content-capability",
  finance: "content-capability",
  hrm: "content-capability",
  "platform-governance": "content-capability",
  "platform-administration": "content-capability",
  iam: "content-capability",
  observability: "content-capability",
  "user-workspace": "surface",
};

export function baseStyleClassForL1(capabilityId: string): BaseStyleNavClass | null {
  return BASE_STYLE_L1_CLASS[capabilityId] ?? null;
}

/** Ops: full record payload (not list preview). */
export function intakeRecordDetailPath(recordId: string): string {
  return `${ROUTES.INTAKE_RECORDS}/${encodeURIComponent(recordId)}`;
}

/** Single AI audit entry (admin investigation workspace). */
export function governanceAiAuditEntryPath(auditId: string): string {
  return `${ROUTES.GOVERNANCE_AI_AUDIT}/${encodeURIComponent(auditId)}`;
}

/** Single inbox draft workspace (thread + draft + actions). */
export function intakeDraftEntryPath(recordId: string): string {
  return `${ROUTES.INTAKE_DRAFTS}/${encodeURIComponent(recordId)}`;
}

/** Work surfaces that deep-link into Intake Core draft entry. */
export function workDraftEntryPath(recordId: string): string {
  return intakeDraftEntryPath(recordId);
}

/** Single project workspace detail route. */
export function workProjectDetailPath(projectId: string, options?: { recordId?: string }): string {
  const base = `${ROUTES.WORK_PROJECTS}/${encodeURIComponent(projectId)}`;
  const recordId = options?.recordId?.trim();
  if (!recordId) return base;
  return `${base}?record_id=${encodeURIComponent(recordId)}`;
}

export type PartyCommercialPane = "contracts" | "payables" | "receivables" | "aging";

export const PARTY_COMMERCIAL_ITEM_QUERY_KEY = "commercial_id";

export type PartyWorkspaceTab = "overview" | "identity" | "sources" | "conversations" | "work" | "commercial" | "timeline" | "intelligence";
export type PartySourcePane = "mail";

export type CommercialPartyRole = "customer" | "supplier" | "partner";

export function partyRoleFromPathname(pathname: string): CommercialPartyRole {
  if (pathname.startsWith(ROUTES.SUPPLIER_DIRECTORY)) return "supplier";
  if (pathname.startsWith(ROUTES.PARTNER_DIRECTORY)) return "partner";
  return "customer";
}

export function partyDirectoryPath(role: CommercialPartyRole): string {
  if (role === "supplier") return ROUTES.SUPPLIER_DIRECTORY;
  if (role === "partner") return ROUTES.PARTNER_DIRECTORY;
  return ROUTES.PARTIES;
}

export function partyIdentityReviewBasePath(_role: CommercialPartyRole): string {
  return ROUTES.PARTY_IDENTITY_REVIEW;
}

/** Role-aware party workspace detail route (customer/supplier/partner directories). */
export function partyProfileDetailPath(
  role: CommercialPartyRole,
  partyId: string,
  options?: {
    tab?: PartyWorkspaceTab;
    conversationKey?: string;
    commercialPane?: PartyCommercialPane;
    commercialItemId?: string;
    sourcePane?: PartySourcePane;
  },
): string {
  const directory = partyDirectoryPath(role);
  const base = `${directory}/${encodeURIComponent(partyId)}`;
  const params = new URLSearchParams();
  const tab = options?.tab?.trim();
  if (tab) params.set("tab", tab);
  const conversationKey = options?.conversationKey?.trim();
  if (conversationKey) params.set("conversation_key", conversationKey);
  const commercialPane = options?.commercialPane?.trim();
  const normalizedPane =
    commercialPane === "finance" ? "receivables" : commercialPane;
  if (
    normalizedPane === "contracts" ||
    normalizedPane === "payables" ||
    normalizedPane === "receivables" ||
    normalizedPane === "aging"
  ) {
    params.set("commercial", normalizedPane);
  }
  const commercialItemId = options?.commercialItemId?.trim();
  if (commercialItemId) params.set(PARTY_COMMERCIAL_ITEM_QUERY_KEY, commercialItemId);
  if (options?.sourcePane === "mail") params.set("source", "mail");
  const query = params.toString();
  if (!query) return base;
  return `${base}?${query}`;
}

/** Single customer profile workspace detail route. */
export function customerProfileDetailPath(
  partyId: string,
  options?: {
    tab?: PartyWorkspaceTab;
    conversationKey?: string;
    commercialPane?: PartyCommercialPane;
    commercialItemId?: string;
  },
): string {
  return partyProfileDetailPath("customer", partyId, options);
}

/** Identity Review queue with optional customer and conversation scope. partyRole is required (fail-closed). */
export function identityReviewPath(options: {
  partyRole: CommercialPartyRole;
  partyId?: string;
  conversationKey?: string;
}): string {
  const conversationKey = options.conversationKey?.trim();
  const base = conversationKey
    ? ROUTES.PARTY_IDENTITY_REVIEW
    : partyIdentityReviewBasePath(options.partyRole);
  const params = new URLSearchParams();
  const partyId = options.partyId?.trim();
  if (partyId) params.set("party_id", partyId);
  if (conversationKey) params.set("conversation_key", conversationKey);
  const query = params.toString();
  if (!query) return base;
  return `${base}?${query}`;
}

/** Conversations advisor queue with preserved selection/filter state. */
export function conversationsQueuePath(options?: {
  conversationKey?: string;
  channel?: string;
  cursor?: string;
  review?: string;
  q?: string;
  binding?: string;
  risk?: string;
  sla?: string;
  audience?: string;
}): string {
  const params = new URLSearchParams();
  const conversationKey = options?.conversationKey?.trim();
  if (conversationKey) params.set("conversation_key", conversationKey);
  const channel = options?.channel?.trim();
  if (channel && channel !== "all") params.set("channel", channel);
  const cursor = options?.cursor?.trim();
  if (cursor) params.set("cursor", cursor);
  const review = options?.review?.trim();
  if (review === "quarantine") params.set("review", "quarantine");
  for (const [key, value] of Object.entries({
    q: options?.q,
    binding: options?.binding,
    risk: options?.risk,
    sla: options?.sla,
    audience: options?.audience,
  })) {
    const cleaned = value?.trim();
    if (cleaned) params.set(key, cleaned);
  }
  const query = params.toString();
  if (!query) return ROUTES.CONVERSATIONS;
  return `${ROUTES.CONVERSATIONS}?${query}`;
}

/** Single contract agreement detail workspace. */
export function contractsAgreementDetailPath(agreementId: string): string {
  return `${ROUTES.CONTRACTS_AGREEMENTS}/${encodeURIComponent(agreementId)}`;
}

/** Contracts hub with optional customer party filter. */
export function contractsAgreementsHubPath(partyId?: string): string {
  const cleaned = partyId?.trim();
  if (!cleaned) return ROUTES.CONTRACTS_AGREEMENTS;
  return `${ROUTES.CONTRACTS_AGREEMENTS}?party_id=${encodeURIComponent(cleaned)}`;
}

/** Finance invoices hub with optional party filter. */
export function financeInvoicesHubPath(partyId?: string): string {
  const cleaned = partyId?.trim();
  if (!cleaned) return ROUTES.FINANCE_INVOICES;
  return `${ROUTES.FINANCE_INVOICES}?party_id=${encodeURIComponent(cleaned)}`;
}

export function financePayablesHubPath(options?: {
  partyId?: string;
  agreementId?: string;
}): string {
  const params = new URLSearchParams();
  const partyId = options?.partyId?.trim() ?? "";
  const agreementId = options?.agreementId?.trim() ?? "";
  if (partyId.length > 0) params.set("party_id", partyId);
  if (agreementId.length > 0) params.set("agreement_id", agreementId);
  const query = params.toString();
  return query.length > 0 ? `${ROUTES.FINANCE_PAYABLES}?${query}` : ROUTES.FINANCE_PAYABLES;
}

/** Strategy goal/objective workspace deep-link (catalog entity_id = objective_id). */
export function strategyGoalDetailPath(goalId: string): string {
  return `${ROUTES.STRATEGY_GOALS}/${encodeURIComponent(goalId)}`;
}

/** Single contract template detail workspace. */
export function contractsTemplateDetailPath(templateUuid: string): string {
  return `${ROUTES.CONTRACTS_TEMPLATES}/${encodeURIComponent(templateUuid)}`;
}

/** Single payment dossier type detail workspace (ĐNTT layout). */
export function financePaymentDossierDetailPath(dossierTypeId: string): string {
  return `${ROUTES.FINANCE_BILLING}/${encodeURIComponent(dossierTypeId)}`;
}

export type SearchPathOptions = {
  sort?: "score" | "event_at";
  offset?: number;
  entityTypes?: readonly string[];
  entityKeys?: readonly string[];
};

/** Search workspace with URL state for shareable semantic result sets. */
export function searchPath(
  query?: string,
  types?: readonly string[],
  contextIntent?: string,
  freshnessMaxAgeDays?: number,
  options?: SearchPathOptions,
): string {
  const params = new URLSearchParams();
  const q = (query ?? "").trim();
  if (q.length > 0) params.set("q", q);
  const normalizedTypes = (types ?? []).map((type) => type.trim()).filter((type) => type.length > 0);
  if (normalizedTypes.length > 0) params.set("types", normalizedTypes.join(","));
  const normalizedContextIntent = (contextIntent ?? "").trim();
  if (normalizedContextIntent.length > 0) params.set("context_intent", normalizedContextIntent);
  if (typeof freshnessMaxAgeDays === "number" && Number.isFinite(freshnessMaxAgeDays) && freshnessMaxAgeDays >= 0) {
    params.set("freshness_max_age_days", String(Math.trunc(freshnessMaxAgeDays)));
  }
  if (options?.sort === "score" || options?.sort === "event_at") {
    params.set("sort", options.sort);
  }
  if (typeof options?.offset === "number" && Number.isFinite(options.offset) && options.offset > 0) {
    params.set("offset", String(Math.trunc(options.offset)));
  }
  const entityTypes = (options?.entityTypes ?? []).map((value) => value.trim()).filter((value) => value.length > 0);
  if (entityTypes.length > 0) params.set("entity_types", entityTypes.join(","));
  const entityKeys = (options?.entityKeys ?? []).map((value) => value.trim()).filter((value) => value.length > 0);
  if (entityKeys.length > 0) params.set("entity_keys", entityKeys.join(","));
  const queryString = params.toString();
  return queryString.length > 0 ? `${ROUTES.SEARCH}?${queryString}` : ROUTES.SEARCH;
}

/** RAG document detail route used by platform search and document list rows. */
export function ragDocumentDetailPath(documentId: string, options?: { fromProjection?: string }): string {
  const base = `${ROUTES.KNOWLEDGE_CORPUS}/${encodeURIComponent(documentId)}`;
  const projection = options?.fromProjection?.trim() ?? "";
  if (projection.length === 0) return base;
  return `${base}?from_projection=${encodeURIComponent(projection)}`;
}

/** Content library item full-detail workspace (deep review beyond sidebar peek). */
export function contentLibraryItemPath(itemId: string, options?: { tab?: "content" | "original" }): string {
  const base = `${ROUTES.KNOWLEDGE_CONTENT_LIBRARY}/${encodeURIComponent(itemId)}`;
  const tab = options?.tab;
  if (tab === "original") return `${base}?tab=original`;
  return base;
}

/** Pre-RAG inbox item detail (staging candidate before searchable RAG). */
export function preRagReviewDetailPath(rowId: string, options?: { entityKey?: string }): string {
  const base = `${ROUTES.KNOWLEDGE_PRE_RAG_REVIEW}/${encodeURIComponent(rowId)}`;
  const entityKey = typeof options?.entityKey === "string" ? options.entityKey.trim() : "";
  if (!entityKey) return base;
  return `${base}?entity_key=${encodeURIComponent(entityKey)}`;
}

/** Single finance invoice detail workspace. */
export function financeInvoiceDetailPath(invoiceId: string): string {
  return `${ROUTES.FINANCE_INVOICES}/${encodeURIComponent(invoiceId)}`;
}

export function financePayableDetailPath(payableId: string): string {
  return `${ROUTES.FINANCE_PAYABLES}/${encodeURIComponent(payableId)}`;
}

/** Single bank account workspace for statement import and allocation. */
export function financeBankAccountDetailPath(
  bankAccountId: string,
  options?: { transactionId?: string; bankView?: string; transactionSide?: "credit" | "debit" },
): string {
  const base = `${ROUTES.FINANCE_BANK}/${encodeURIComponent(bankAccountId)}`;
  const params = new URLSearchParams();
  const bankView = typeof options?.bankView === "string" ? options.bankView.trim() : "";
  const transactionId = typeof options?.transactionId === "string" ? options.transactionId.trim() : "";
  const transactionSide =
    options?.transactionSide === "credit" || options?.transactionSide === "debit" ? options.transactionSide : "";
  if (bankView.length > 0) {
    params.set("bank_view", bankView);
  }
  if (transactionId.length > 0) {
    params.set("bank_tx", transactionId);
  }
  if (transactionSide.length > 0) {
    params.set("bank_tx_side", transactionSide);
  }
  const query = params.toString();
  if (query.length === 0) return base;
  return `${base}?${query}`;
}

/** Tenant-wide bank transaction ledger (all accounts). */
export function financeBankTransactionsPath(): string {
  return ROUTES.FINANCE_BANK_TRANSACTIONS;
}

/** Reconciliation workspace scoped to a single run id already supported by query state. */
export function financeReconciliationRunPath(runId: string): string {
  return `${ROUTES.FINANCE_RECONCILIATION}?run_id=${encodeURIComponent(runId)}`;
}

/** IAM tenant detail workspace. */
export function iamTenantDetailPath(tenantId: string): string {
  return `${ROUTES.ADMIN_AUTHZ_TENANTS}/${encodeURIComponent(tenantId)}`;
}

/** IAM role setup detail workspace. Uses public record identity when available. */
export function iamRoleDetailPath(roleRecordId: string): string {
  return `${ROUTES.ADMIN_AUTHZ_ROLES}/${encodeURIComponent(roleRecordId)}`;
}

/** Nested role detail under a tenant (Parent Control Console / tenant Roles tab). */
export function iamTenantRoleDetailPath(tenantId: string, roleRecordId: string): string {
  return `${iamTenantDetailPath(tenantId)}/roles/${encodeURIComponent(roleRecordId)}`;
}

/** Single task workspace detail route. */
export function workTaskDetailPath(taskId: string): string {
  return `${ROUTES.WORK_TASKS}/${encodeURIComponent(taskId)}`;
}

/** Tasks list scoped to the current user's assigned tasks (My responsibility deep-link). */
export function workTasksMyListPath(): string {
  return `${ROUTES.WORK_TASKS}?view=my`;
}

/** Tasks list filtered to status New (dashboard Unassigned KPI). Members: view=my. */
export function workTasksUnassignedListPath(options?: { memberScope?: boolean }): string {
  const params = new URLSearchParams();
  params.set("status", "new");
  if (options?.memberScope) {
    params.set("view", "my");
  }
  return `${ROUTES.WORK_TASKS}?${params.toString()}`;
}

/** Projects list scoped to the current user's lead projects (My responsibility deep-link). */
export function workProjectsMyListPath(): string {
  return `${ROUTES.WORK_PROJECTS}?view=my`;
}

/** Projects list on At risk tab; optional `q` pre-fills search. */
export function workProjectsAtRiskSearchPath(searchQuery?: string): string {
  const q = (searchQuery ?? "").trim();
  if (!q) return `${ROUTES.WORK_PROJECTS}?view=at_risk`;
  return `${ROUTES.WORK_PROJECTS}?view=at_risk&q=${encodeURIComponent(q)}`;
}

/** Tasks list; optional `project_id` query opens with project filter (e.g. after inbox approve). */
export function workTasksListPath(options?: { projectId?: string }): string {
  const projectId = (options?.projectId ?? "").trim();
  if (!projectId) return ROUTES.WORK_TASKS;
  return `${ROUTES.WORK_TASKS}?project_id=${encodeURIComponent(projectId)}`;
}

const FULL_BLEED_LAYOUT_PATHS: readonly string[] = [
  ROUTES.DASHBOARD,
  ROUTES.SEARCH,
  ROUTES.PLATFORM_ANALYTICS,
  ROUTES.PLATFORM_MONITORING,
  ROUTES.RANKING,
  ROUTES.INTAKE_DRAFTS,
  ROUTES.INTAKE_RECORDS,
  ROUTES.INTAKE_DIGEST,
  ROUTES.INTAKE_LEDGER,
  ROUTES.MAILBOX,
  ROUTES.CONVERSATIONS,
  ROUTES.CONVERSATIONS_CHANNEL_SCOPE,
  ROUTES.LEGACY_NOTIFICATION,
  ROUTES.WORK_PROJECTS,
  ROUTES.LEGACY_HUB_DATA,
  ROUTES.PARTIES,
  ROUTES.SUPPLIER_DIRECTORY,
  ROUTES.PARTNER_DIRECTORY,
  ROUTES.WORK_TASKS,
  ROUTES.WORK_CUSTOM_VIEW,
  ROUTES.WORK_ANALYTICS,
  ROUTES.STRATEGY,
  ROUTES.STRATEGY_GOALS,
  ROUTES.STRATEGY_ALIGNMENT,
  ROUTES.STRATEGY_REVIEWS,
  ROUTES.USER_GUIDE,
  ROUTES.USER_ACCOUNT_SETTINGS,
  ROUTES.GOVERNANCE_DRAFT_TEMPLATES,
  ROUTES.GOVERNANCE_TRIAGE_TEMPLATES,
  ROUTES.GOVERNANCE_THREAD_DIGEST_TEMPLATES,
  ROUTES.GOVERNANCE_REQUEST_INFO_TEMPLATES,
  ROUTES.GOVERNANCE_AI_AUDIT,
  ROUTES.GOVERNANCE_AI_PRIVACY_RULES,
  ROUTES.CONTRACTS_AGREEMENTS,
  ROUTES.CONTRACTS_TEMPLATES,
  ROUTES.FINANCE,
  ROUTES.FINANCE_INVOICES,
  ROUTES.FINANCE_PAYABLES,
  ROUTES.FINANCE_BILLING,
  ROUTES.FINANCE_PAYMENT_DOSSIERS,
  ROUTES.FINANCE_BANK,
  ROUTES.FINANCE_BANK_TRANSACTIONS,
  ROUTES.FINANCE_RECONCILIATION,
  ROUTES.FINANCE_REPORTING,
  ROUTES.FINANCE_CONTENT_REVIEW,
  ROUTES.HRM,
  ROUTES.HRM_LEGAL_ENTITIES,
  ROUTES.HRM_COMPANY_LEVELS,
  ROUTES.HRM_BUSINESS_LINES,
  ROUTES.HRM_EMPLOYEE_GROUPS,
  ROUTES.HRM_SPECIALTIES,
  ROUTES.HRM_POSITIONS,
  ROUTES.HRM_HEADCOUNTS,
  ROUTES.HRM_EMPLOYEES,
  ROUTES.KNOWLEDGE_PLATFORM,
  ROUTES.KNOWLEDGE_CORPUS,
  ROUTES.KNOWLEDGE_PRE_RAG_REVIEW,
  ROUTES.KNOWLEDGE_INGEST,
  ROUTES.KNOWLEDGE_CONTENT,
  ROUTES.KNOWLEDGE_CONTENT_CAPTURE,
  ROUTES.KNOWLEDGE_CONTENT_REVIEW,
  ROUTES.KNOWLEDGE_CONTENT_LIBRARY,
  ROUTES.KNOWLEDGE_CONTENT_STORAGE,
  ROUTES.KNOWLEDGE_OPS,
  ROUTES.KNOWLEDGE_EMBEDDINGS,
  ROUTES.KNOWLEDGE_SEARCH_LAB,
  ROUTES.KNOWLEDGE_EVALUATION,
  ROUTES.CONTENT,
  ROUTES.CONTENT_CAPTURE,
  ROUTES.CONTENT_REVIEW,
  ROUTES.GOVERNANCE_RULES,
  ROUTES.GOVERNANCE_ADMINISTRATION,
  ROUTES.GOVERNANCE_OPERATIONAL_SETTINGS,
  ROUTES.GOVERNANCE_INTEGRATIONS,
  ROUTES.GOVERNANCE_SYNC_DIAGNOSTICS,
  ROUTES.GOVERNANCE_CONTEXT_CATALOG,
  ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT,
  ROUTES.GOVERNANCE_CONTEXT_DRIFT,
  ROUTES.GOVERNANCE_CONTEXT_OPS,
  ROUTES.DATA_GOVERNANCE,
  ROUTES.DATA_GOVERNANCE_CATALOG,
  ROUTES.DATA_GOVERNANCE_POLICY,
  ROUTES.DATA_GOVERNANCE_DRIFT,
  ROUTES.DATA_GOVERNANCE_PROJECTION,
  ROUTES.ADMIN_AUTHZ,
  ROUTES.ADMIN_AUTHZ_ROLES,
  ROUTES.AI_PLATFORM,
  ROUTES.AI_WORKFLOW_POLICY,
  ROUTES.ADMIN_RULES,
  ROUTES.ADMIN_TEMPLATE_ROUTING_RULES,
  ROUTES.ADMIN_LEDGER,
];

export const FULL_BLEED_LAYOUT_PATH_PREFIXES: readonly string[] = [...FULL_BLEED_LAYOUT_PATHS, "/settings/me", "/operate", "/admin"];

export const WORKSPACE_PATH_PREFIXES: readonly string[] = FULL_BLEED_LAYOUT_PATH_PREFIXES;

export function isFullBleedLayoutPath(pathname: string): boolean {
  const normalized = normalizeLegacyRoutePath(pathname);
  return pathMatchesAnyPrefix(normalized, FULL_BLEED_LAYOUT_PATH_PREFIXES);
}

/** Compatibility wrapper for callers that still use the legacy full-bleed name. */
export function isWorkspaceLayoutPath(pathname: string): boolean {
  return isFullBleedLayoutPath(pathname);
}

const PLATFORM_SURFACE_PATHS: readonly string[] = [ROUTES.ROOT, ROUTES.ADMIN_AUTHZ, "/platform", "/services", "/ai", "/rag", "/knowledge", "/ranking", "/analytics", "/monitoring"];

function pathMatchesAnyPrefix(pathname: string, prefixes: readonly string[]): boolean {
  for (const prefix of prefixes) {
    if (pathname === prefix) return true;
    if (pathname.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

export function normalizeLegacyRoutePath(pathname: string): string {
  if (pathname === ROUTES.LEGACY_HUB_DATA || pathname === `${ROUTES.LEGACY_HUB_DATA}/`) {
    return ROUTES.PARTIES;
  }
  if (pathname === "/customers" || pathname === "/customers/") {
    return ROUTES.PARTIES;
  }
  if (pathname.startsWith("/customers/")) {
    return pathname.replace(/^\/customers/, ROUTES.PARTIES);
  }
  if (pathname === ROUTES.SUPPLIER_IDENTITY_REVIEW || pathname.startsWith(`${ROUTES.SUPPLIER_IDENTITY_REVIEW}/`)) {
    return ROUTES.SUPPLIER_DIRECTORY;
  }
  if (pathname === ROUTES.PARTNER_IDENTITY_REVIEW || pathname.startsWith(`${ROUTES.PARTNER_IDENTITY_REVIEW}/`)) {
    return ROUTES.PARTNER_DIRECTORY;
  }
  if (pathname === "/draft" || pathname.startsWith("/draft/")) {
    return pathname.replace(/^\/draft/, ROUTES.INTAKE_DRAFTS);
  }
  if (pathname === "/finance/statements" || pathname === "/finance/statements/") {
    return ROUTES.FINANCE_BANK;
  }
  if (pathname === "/finance/reports" || pathname === "/finance/reports/") {
    return ROUTES.FINANCE_REPORTING;
  }
  if (
    pathname === "/finance/billing-profile" ||
    pathname === "/finance/billing-profile/" ||
    pathname === "/finance/receiving-identity" ||
    pathname === "/finance/receiving-identity/" ||
    pathname === "/finance/payment-dossiers" ||
    pathname === "/finance/payment-dossiers/"
  ) {
    return ROUTES.FINANCE_BILLING;
  }
  if (pathname === "/policy" || pathname === "/policy/") {
    return ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT;
  }
  if (pathname === "/notification" || pathname === "/notification/") {
    return ROUTES.INTAKE_DIGEST;
  }
  if (pathname.startsWith("/intake/tasks/") || pathname === "/intake/tasks") {
    return pathname.replace(/^\/intake\/tasks/, ROUTES.WORK_TASKS);
  }
  if (pathname.startsWith("/intake/records/") || pathname === "/intake/records") {
    return pathname.replace(/^\/intake\/records/, ROUTES.INTAKE_RECORDS);
  }
  if (pathname.startsWith("/intake/threads/") || pathname === "/intake/threads") {
    return pathname.replace(/^\/intake\/threads/, ROUTES.INTAKE_DRAFTS);
  }
  if (pathname.startsWith("/contracts/agreements/") || pathname === "/contracts/agreements") {
    return pathname.replace(/^\/contracts\/agreements/, ROUTES.CONTRACTS_AGREEMENTS);
  }
  if (pathname.startsWith("/content/library")) {
    return pathname.replace(/^\/content\/library/, ROUTES.KNOWLEDGE_CONTENT_LIBRARY);
  }
  // Legacy Hub/Admin URLs are compatibility-only; product IA owns this under AI Platform.
  if (pathname === "/admin/llm-runtime" || pathname === "/admin/llm-runtime/") {
    return ROUTES.AI_WORKFLOW_POLICY;
  }
  if (pathname === "/llm-runtime" || pathname === "/llm-runtime/") {
    return ROUTES.AI_WORKFLOW_POLICY;
  }
  if (pathname === "/admin/authz") return ROUTES.ADMIN_AUTHZ_TENANTS;
  if (pathname === "/admin/authz/tenants") return ROUTES.ADMIN_AUTHZ_TENANTS;
  if (pathname === "/admin/authz/roles") return ROUTES.ADMIN_AUTHZ_ROLES;
  if (pathname === "/admin/authz/users") return ROUTES.ADMIN_AUTHZ_USERS;
  if (pathname === "/admin/authz/audit") return ROUTES.ADMIN_AUTHZ_AUDIT;
  if (pathname === "/admin/authz/simulator") return ROUTES.ADMIN_AUTHZ_SIMULATOR;
  if (pathname === "/ranking" || pathname === "/ranking/") {
    return ROUTES.RANKING;
  }
  if (pathname === ROUTES.DATA_GOVERNANCE || pathname === `${ROUTES.DATA_GOVERNANCE}/`) {
    return ROUTES.GOVERNANCE_CONTEXT_CATALOG;
  }
  if (pathname === ROUTES.DATA_GOVERNANCE_CATALOG) {
    return ROUTES.GOVERNANCE_CONTEXT_CATALOG;
  }
  if (pathname === ROUTES.DATA_GOVERNANCE_POLICY) {
    return ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT;
  }
  if (pathname === ROUTES.DATA_GOVERNANCE_DRIFT) {
    return ROUTES.GOVERNANCE_CONTEXT_DRIFT;
  }
  if (pathname === ROUTES.DATA_GOVERNANCE_PROJECTION) {
    return ROUTES.GOVERNANCE_CONTEXT_OPS;
  }
  if (pathname === "/intake-hub" || pathname === "/intake-hub/") return ROUTES.DASHBOARD;
  if (!pathname.startsWith("/intake-hub/")) return pathname;
  const tail = pathname.slice("/intake-hub".length);
  if (tail === "/admin" || tail === "admin") return ROUTES.GOVERNANCE_ADMINISTRATION;
  const normalized = tail.startsWith("/") ? tail : `/${tail}`;
  if (normalized === "/admin/authz") return ROUTES.ADMIN_AUTHZ_TENANTS;
  if (normalized === "/admin/authz/tenants") return ROUTES.ADMIN_AUTHZ_TENANTS;
  if (normalized === "/admin/authz/roles") return ROUTES.ADMIN_AUTHZ_ROLES;
  if (normalized === "/admin/authz/users") return ROUTES.ADMIN_AUTHZ_USERS;
  if (normalized === "/admin/authz/audit") return ROUTES.ADMIN_AUTHZ_AUDIT;
  if (normalized === "/admin/authz/simulator") return ROUTES.ADMIN_AUTHZ_SIMULATOR;
  if (normalized === "/notification") return ROUTES.INTAKE_DIGEST;
  if (normalized === "/analytics") return ROUTES.WORK_ANALYTICS;
  if (normalized === ROUTES.LEGACY_HUB_DATA) return ROUTES.PARTIES;
  if (normalized === "/llm-runtime" || normalized === "/admin/llm-runtime") return ROUTES.AI_WORKFLOW_POLICY;
  return normalized;
}

export function isTrustedInAppPath(pathname: string): boolean {
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return true;
  if (pathMatchesAnyPrefix(pathname, PLATFORM_SURFACE_PATHS)) return true;
  return pathMatchesAnyPrefix(pathname, FULL_BLEED_LAYOUT_PATHS);
}

/** Normalizes Gateway-provided deep links before navigation. */
export function normalizeTrustedInAppPath(rawPath: string): string | null {
  const raw = rawPath.trim();
  if (!raw.startsWith("/") || raw.includes("..")) return null;
  try {
    const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
    const url = new URL(raw, origin);
    const pathname = normalizeLegacyRoutePath(url.pathname);
    if (pathname === ROUTES.FINANCE_BANK) {
      const accountId = url.searchParams.get("account_id")?.trim() ?? "";
      if (accountId.length > 0) {
        return financeBankAccountDetailPath(accountId);
      }
    }
    // Legacy content deep links used ?q=<itemId>; prefer the item detail workspace.
    if (pathname === ROUTES.KNOWLEDGE_CONTENT_LIBRARY || pathname === ROUTES.CONTENT_LIBRARY) {
      const legacyItemId = url.searchParams.get("q")?.trim() ?? "";
      if (/^[a-f0-9]{16,64}$/i.test(legacyItemId)) {
        return contentLibraryItemPath(legacyItemId);
      }
    }
    if (!isTrustedInAppPath(pathname)) return null;
    return `${pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

